import { useEffect, useRef, useState, type ButtonHTMLAttributes, type KeyboardEvent, type ReactNode } from 'react'
import { Emblem, type EmblemName } from './brand/Emblems'
import { PlankButton } from './Plank'
import { usePress, type Press } from './press'
import { cx } from './cx'

/* Actions live on the thing they act on (docs/game_bible/ui_actions_v04.md):
   a plank in a card, a price at the end of a row, a chip next to a record,
   and a question that appears in the place of the button it guards. */

const BUSY = 'Подождите…'

type Base = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'children'> & {
  onPress?: Press
  locked?: boolean
  children: ReactNode
}

/** A card's plank that runs a command: shows «Подождите…» and never sends twice. */
export function ActionButton({ onPress, locked, gold, children, className, innerClassName, ...rest }: Base & { gold?: boolean; innerClassName?: string }) {
  const { busy, disabled, press } = usePress(onPress, locked)
  return (
    <PlankButton {...rest} gold={gold} locked={locked} disabled={disabled} className={cx(busy && 'is-busy', className)} innerClassName={innerClassName}
      aria-busy={busy || undefined} onClick={press}>
      {busy ? BUSY : children}
    </PlankButton>
  )
}

/** The action at the end of a list row: a price, «Надеть», «Сделать». */
export function PriceButton({ onPress, locked, children, className, ...rest }: Base) {
  const { busy, disabled, press } = usePress(onPress, locked)
  return (
    <button type="button" {...rest} className={cx('pricebtn', locked && 'is-locked', busy && 'is-busy', className)} disabled={disabled}
      aria-busy={busy || undefined} onClick={press}>
      {busy ? '…' : children}
    </button>
  )
}

/** The action next to a record in a list or on its page: «Забрать», «В путь», «Взять». */
export function RowAction({ onPress, locked, gold, big, emblem, children, className, ...rest }: Base & { gold?: boolean; big?: boolean; emblem?: EmblemName }) {
  const { busy, disabled, press } = usePress(onPress, locked)
  return (
    <button type="button" {...rest} className={cx('rchip', gold && 'rchip--gold', big && 'rchip--big', busy && 'is-busy', className)} disabled={disabled}
      aria-busy={busy || undefined} onClick={press}>
      {emblem && <Emblem name={emblem} size={16} />}{busy ? BUSY : children}
    </button>
  )
}

type ConfirmProps = {
  /** The question in place of the trigger: «Купить «Топорик» за 60 серебра?». */
  question: ReactNode
  yes?: string
  no?: string
  onConfirm: () => Promise<unknown> | void
  /** Renders the trigger; calling `ask` swaps it for the question. */
  children: (ask: () => void) => ReactNode
  /** `row` — the question fills a list row (price buttons); `card` — a line in a card (planks). */
  kind?: 'row' | 'card'
  className?: string
}

/** Asks in the same place the player pressed: the trigger turns into a
 *  question with «Да» and «Нет»; only «Да» runs the command. */
export function ConfirmInPlace({ question, yes = 'Да', no = 'Нет', onConfirm, children, kind = 'card', className }: ConfirmProps) {
  const [asking, setAsking] = useState(false)
  const noRef = useRef<HTMLButtonElement>(null)
  useEffect(() => { if (asking) noRef.current?.focus() }, [asking])
  if (!asking) return <>{children(() => setAsking(true))}</>
  const confirm = async () => {
    await onConfirm()
    setAsking(false)
  }
  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setAsking(false) }
  if (kind === 'row') {
    return (
      <div className={cx('srow srow--ask', className)} role="group" aria-label="Подтверждение" onKeyDown={onKey}>
        <span className="srow__q">{question}</span>
        <button type="button" ref={noRef} className="pricebtn pricebtn--no" onClick={() => setAsking(false)}>{no}</button>
        <PriceButton onPress={confirm}>{yes}</PriceButton>
      </div>
    )
  }
  return (
    <div className={cx('askline', className)} role="group" aria-label="Подтверждение" onKeyDown={onKey}>
      <span>{question}</span>
      <PlankButton ref={noRef} onClick={() => setAsking(false)}>{no}</PlankButton>
      <ActionButton gold onPress={confirm}>{yes}</ActionButton>
    </div>
  )
}
